import { motion, useReducedMotion } from 'motion/react';
import { Button } from './Button';

/**
 * "Gaming @ State Farm" — secondary marketing blurb pointing the visitor at
 * State Farm's gaming sponsorships landing page on statefarm.com (external,
 * opens in new tab).
 *
 * Layout:
 *   - md+ : heading + Learn More on the same row, body text in two columns
 *   - <md : everything stacks vertically (heading, button, divider, two
 *           paragraphs in sequence)
 *
 * Font sizing — each text element has TWO clamps, one for mobile and one
 * for tablet+desktop, picked up at the md (768px) breakpoint via the `md:`
 * variant. Both clamps share their meeting value at md so the transition
 * is seamless. Tune the numbers per element; the structure is:
 *   text-[clamp(<mobile-min>, <vw>, <mobile-max>)]
 *   md:text-[clamp(<desktop-min>, <vw>, <desktop-max>)]
 *
 * The Plausible outbound-links auto-event captures the Learn More click as
 * "Outbound Link: Click" with {url} — no custom event needed here.
 */
const LEARN_MORE_URL = 'https://www.statefarm.com/lp/ct1/gaming';

export default function SfGamingSection() {
  const reduce = useReducedMotion();

  return (
    <motion.section
      aria-labelledby="sf-gaming-heading"
      className="relative overflow-hidden bg-gradient-to-t from-[#c71635] to-[#f4002b] px-7 pb-12 pt-10 sm:px-16 sm:py-12 md:p-12"
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={reduce ? { duration: 0 } : { duration: 0.4, ease: 'easeOut' }}
    >
      <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-6 sm:gap-8">
        {/* Heading row + Learn More CTA */}
        <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between md:gap-12">
          <h2
            id="sf-gaming-heading"
            className="font-[Orbitron] font-normal uppercase text-white leading-[0.95] tracking-[-0.03em] text-[clamp(28px,9vw,36px)] md:text-[clamp(36px,4vw,48px)]"
          >
            <span>Gaming </span>
            <span className="text-[#d08dff]">@</span>
            <br />
            <span>State Farm</span>
          </h2>

          <Button
            variant="dark"
            href={LEARN_MORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="min-w-[200px] shrink-0"
          >
            Learn More
          </Button>
        </div>

        {/* Divider between heading row and body text */}
        <div aria-hidden="true" className="h-[2px] w-full bg-[#FF647E]" />

        {/* Body text — two columns on md+, stacked on mobile */}
        <div className="flex flex-col gap-6 text-white md:flex-row md:gap-[25px]">
          <p className="flex-1 font-[Arial] leading-[1.09] text-[clamp(16px,5vw,22px)] md:text-[clamp(18px,1.6vw,20px)]">
            Gaming is more than a hobby. It&rsquo;s how people connect, compete, and build community.
            <br />
            <br />
            <span className="font-[Arial] font-bold italic">
              And we&rsquo;re right there with them.
            </span>
          </p>
          <p className="flex-1 font-[Arial] leading-[1.35] text-[clamp(16px,5vw,22px)] md:text-[clamp(18px,1.6vw,20px)]">
            At State Farm<sup className="text-[0.6em] top-[-0.5em]">&reg;</sup>, we bring people together through live events, creator collaborations, and
            original content, from competitions to digital experiences.
          </p>
        </div>
      </div>
    </motion.section>
  );
}
